/**
 * Product-card swatch availability.
 *
 * Client-rendered cards (Athos grids, carousels) build their colour
 * swatches from the raw `ss_swatches` feed field, which carries no
 * sibling product status — archived siblings render as dead swatches
 * and sold-out siblings look purchasable. This module validates every
 * swatch handle on the page against the Storefront API in one batched
 * GraphQL request per result set:
 *
 *   - handle resolves to null (archived / draft / unpublished on this
 *     market's store) → the swatch is removed and the `+N` overflow
 *     toggle is rebalanced;
 *   - `availableForSale` is false → the swatch is crossed out via
 *     `data-sold-out="true"` but stays clickable so the swap-to-notify-me
 *     flow remains reachable.
 *
 * The active swatch is crossed out too when its own product is sold out,
 * so the card's current colour reads as unavailable at a glance.
 *
 * Statuses are cached per handle for the page lifetime, so pagination
 * and card swaps only query handles not yet seen. A MutationObserver
 * picks up dynamically injected cards (grid renders, colour swaps).
 * Fetch failures fail open — swatches are left as rendered — and the
 * module disables itself after repeated failures.
 *
 * Until a container is validated, its swatches render as neutral
 * skeleton dots (CSS keyed on `data-availability-resolved` in
 * `snippets/product-card.liquid`) so shoppers never see swatches that
 * are about to be removed or greyed. Every terminal path — applied,
 * failed, disabled, or no token — resolves containers, so the dots
 * always reveal; no-JS visitors never enter the skeleton state.
 */
(function () {
  'use strict';

  if (window.ProductCardAvailability) return;

  // Both swatch hooks share the `.product-card__colours` container class
  // and its skeleton state: grid/carousel cards use `product-card-colour`,
  // the quick-add drawer builds its row with `quick-add-colour`.
  const SWATCH_HOOKS = ['product-card-colour', 'quick-add-colour'];
  const SWATCH_ANY = SWATCH_HOOKS.map((hook) => `[data-js="${hook}"]`).join(', ');
  const SWATCH_UNCHECKED = SWATCH_HOOKS.map(
    (hook) => `[data-js="${hook}"][data-handle]:not([data-availability-checked])`,
  ).join(', ');
  const SWATCH_OVERFLOW = SWATCH_HOOKS.map(
    (hook) => `[data-js="${hook}"][data-overflow="true"]`,
  ).join(', ');
  const CONTAINER_SELECTOR = '[data-js="colour-swatches"]';
  const TOGGLE_SELECTOR = '[data-js="colour-overflow-toggle"]';
  const ENDPOINT = '/api/2026-04/graphql.json';
  const BATCH_SIZE = 100;
  const MAX_FAILURES = 3;

  /** @type {Map<string, { exists: boolean, available: boolean }>} */
  const statusCache = new Map();

  /** @type {Set<string>} Handles with a request currently in flight. */
  const pending = new Set();

  let failureCount = 0;

  /**
   * Reveal a swatch container: swatches render as neutral skeleton dots
   * (see `snippets/product-card.liquid`) until this attribute lands, so
   * shoppers never see swatches that are about to be removed or greyed.
   * @param {HTMLElement} container - A `colour-swatches` wrapper.
   * @returns {void}
   */
  function resolveContainer(container) {
    container.dataset.availabilityResolved = 'true';
  }

  /**
   * Reveal every container regardless of validation state — the fail-open
   * path when validation cannot run or a request fails.
   * @returns {void}
   */
  function resolveAllContainers() {
    document
      .querySelectorAll(`${CONTAINER_SELECTOR}:not([data-availability-resolved])`)
      .forEach(resolveContainer);
  }

  /**
   * Reveal only containers whose swatches have all been checked; ones
   * still awaiting an in-flight batch stay in their skeleton state.
   * @returns {void}
   */
  function resolveSettledContainers() {
    document
      .querySelectorAll(`${CONTAINER_SELECTOR}:not([data-availability-resolved])`)
      .forEach((container) => {
        if (!container.querySelector(SWATCH_UNCHECKED)) resolveContainer(container);
      });
  }

  /**
   * Read the Storefront API token injected by `layout/theme.liquid`.
   * @returns {string} The token, or an empty string when unconfigured.
   */
  function getToken() {
    if (!window.themeSettings || !window.themeSettings.storefrontApiToken) return '';
    return String(window.themeSettings.storefrontApiToken);
  }

  /**
   * Rebuild a single active self-swatch for the card's own product,
   * mirroring the zero-valid-siblings fallback in
   * `templates/product.json.liquid`. Feed data can list only archived
   * siblings without a self entry — removing them all would otherwise
   * leave the card with no swatch at all.
   * @param {HTMLElement} container - An emptied `colour-swatches` wrapper.
   * @returns {void}
   */
  function insertSelfSwatch(container) {
    const card = container.closest('[data-js="product-card"]');
    if (!card || !card.dataset.productHandle) return;
    const nameEl = card.querySelector('[data-js="product-card-colour-name"]');
    const colourName = nameEl ? nameEl.textContent.trim() : '';
    if (!colourName) return;
    const cssColour = colourName.toLowerCase().replace(/\s+/g, '');
    const swatch = document.createElement('a');
    swatch.className = 'product-card__colour';
    swatch.dataset.js = 'product-card-colour';
    swatch.dataset.handle = card.dataset.productHandle;
    swatch.dataset.colourName = colourName;
    swatch.dataset.active = 'true';
    swatch.dataset.availabilityChecked = 'true';
    swatch.href = `/products/${card.dataset.productHandle}`;
    swatch.style.setProperty(
      '--bg-colour',
      window.AthosAPI && typeof window.AthosAPI.safeColour === 'function'
        ? window.AthosAPI.safeColour(cssColour, 'transparent')
        : cssColour,
    );
    swatch.setAttribute('aria-label', colourName);
    container.append(swatch);
  }

  /**
   * Remove an archived swatch and rebalance the `+N` overflow toggle:
   * promote the first hidden overflow swatch into the visible row when
   * a visible swatch was removed, then update or remove the toggle to
   * match the remaining hidden count.
   * @param {HTMLElement} swatch - The swatch anchor to remove.
   * @param {HTMLElement | null} container - Its `colour-swatches` wrapper.
   * @returns {void}
   */
  function removeSwatch(swatch, container) {
    const wasOverflow = swatch.dataset.overflow === 'true';
    swatch.remove();
    if (!container) return;

    const toggle = container.querySelector(TOGGLE_SELECTOR);
    if (!toggle) return;

    if (!wasOverflow) {
      const promoted = container.querySelector(SWATCH_OVERFLOW);
      if (promoted) {
        delete promoted.dataset.overflow;
        toggle.before(promoted);
      }
    }

    const hiddenCount = container.querySelectorAll(SWATCH_OVERFLOW).length;
    if (hiddenCount === 0) {
      toggle.remove();
      return;
    }
    toggle.textContent = `+${hiddenCount}`;
    const label = toggle.getAttribute('aria-label');
    if (label) toggle.setAttribute('aria-label', label.replace(/\d+/, String(hiddenCount)));
  }

  /**
   * Remove an archived swatch, then rebuild the self-swatch if the
   * container was emptied.
   * @param {HTMLElement} swatch - The swatch anchor to remove.
   * @param {HTMLElement | null} container - Its `colour-swatches` wrapper.
   * @returns {void}
   */
  function removeAndBackfill(swatch, container) {
    removeSwatch(swatch, container);
    if (container && !container.querySelector(SWATCH_ANY)) {
      insertSelfSwatch(container);
    }
  }

  /**
   * Cross out a sold-out swatch while keeping it clickable, and extend its
   * accessible name with the translated sold-out text.
   * @param {HTMLElement} swatch - The swatch anchor.
   * @param {HTMLElement | null} container - Its `colour-swatches` wrapper.
   * @returns {void}
   */
  function markSoldOut(swatch, container) {
    swatch.dataset.soldOut = 'true';
    const soldOutText = container ? container.dataset.i18nSoldOut : '';
    if (!soldOutText) return;
    const base =
      swatch.dataset.colourName ||
      swatch.getAttribute('aria-label') ||
      swatch.dataset.handle;
    const labelled = `${base} — ${soldOutText}`;
    swatch.setAttribute('aria-label', labelled);
    swatch.setAttribute('title', labelled);
  }

  /**
   * Apply a cached handle status to one swatch and stamp it checked.
   * @param {HTMLElement} swatch - The swatch anchor.
   * @param {{ exists: boolean, available: boolean }} status - Cached status.
   * @returns {void}
   */
  function applyStatus(swatch, status) {
    swatch.dataset.availabilityChecked = 'true';
    const container = swatch.closest(CONTAINER_SELECTOR);
    if (!status.exists) {
      removeAndBackfill(swatch, container);
      return;
    }
    if (!status.available) {
      markSoldOut(swatch, container);
    }
  }

  /**
   * Query the Storefront API for a batch of handles and cache each
   * handle's existence and `availableForSale` state. A handle that
   * resolves to null is archived, draft, or unpublished on this store.
   * @param {string[]} handles - Product handles, at most BATCH_SIZE.
   * @returns {Promise<void>}
   */
  async function fetchBatch(handles) {
    const fields = handles
      .map(
        (handle, i) =>
          `p${i}: product(handle: ${JSON.stringify(handle)}) { availableForSale }`,
      )
      .join(' ');
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': getToken(),
      },
      body: JSON.stringify({ query: `{ ${fields} }` }),
    });
    if (response.status === 401 || response.status === 403) {
      // A rejected token cannot recover within the page lifetime —
      // this store's storefront_api_token setting needs its own token.
      failureCount = MAX_FAILURES;
      throw new Error(`Storefront API ${response.status}`);
    }
    if (!response.ok) throw new Error(`Storefront API ${response.status}`);
    const payload = await response.json();
    if (!payload || !payload.data) throw new Error('Storefront API empty response');
    handles.forEach((handle, i) => {
      const node = payload.data[`p${i}`];
      statusCache.set(handle, {
        exists: Boolean(node),
        available: Boolean(node && node.availableForSale),
      });
    });
  }

  /**
   * Validate every unchecked swatch on the page: apply cached statuses
   * immediately, then batch-fetch unknown handles and re-run once the
   * cache is populated. Failed batches leave their swatches unchecked
   * so a later scan can retry, up to MAX_FAILURES.
   * @returns {Promise<void>}
   */
  async function scan() {
    if (failureCount >= MAX_FAILURES || !getToken()) {
      resolveAllContainers();
      return;
    }

    const swatches = document.querySelectorAll(SWATCH_UNCHECKED);
    if (!swatches.length) {
      resolveSettledContainers();
      return;
    }

    const unknown = new Set();
    swatches.forEach((swatch) => {
      const handle = swatch.dataset.handle;
      const status = statusCache.get(handle);
      if (status) {
        applyStatus(swatch, status);
      } else if (!pending.has(handle)) {
        unknown.add(handle);
      }
    });
    if (!unknown.size) {
      resolveSettledContainers();
      return;
    }

    const queue = Array.from(unknown);
    queue.forEach((handle) => pending.add(handle));

    const batches = [];
    for (let i = 0; i < queue.length; i += BATCH_SIZE) {
      batches.push(queue.slice(i, i + BATCH_SIZE));
    }

    try {
      await Promise.all(batches.map(fetchBatch));
      failureCount = 0;
    } catch (error) {
      failureCount += 1;
      resolveAllContainers();
    } finally {
      queue.forEach((handle) => pending.delete(handle));
    }

    if (statusCache.size) scan();
  }

  const scheduleScan =
    window.themeUtils && typeof window.themeUtils.debounce === 'function'
      ? window.themeUtils.debounce(scan, 150)
      : scan;

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches(SWATCH_ANY) || node.querySelector(SWATCH_ANY)) {
          scheduleScan();
          return;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  scan();

  window.ProductCardAvailability = Object.freeze({ refresh: scan });
})();
